import { Checkbox } from '@/components/ui/checkbox'
import { Field } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import { Select } from '@/components/ui/select'
import { ChipListInput } from '@/components/vendor-profile/ChipListInput'
import {
  DIETARY_TAGS,
  DIETARY_TAG_LABELS,
  FLORAL_PIECES,
  FLORAL_PIECE_LABELS,
  PLANNING_TIERS,
  PLANNING_TIER_LABELS,
  type EquipmentDetails,
  type OfferingServiceDetails,
} from '@lib/input/vendor-offering.input'

interface Props {
  value: OfferingServiceDetails
  onChange: (value: OfferingServiceDetails) => void
}

/** A number box's value: empty is "not given". */
function numberOrNull(text: string, integer: boolean): number | null {
  if (text.trim() === '') return null
  const n = integer ? Number.parseInt(text, 10) : Number.parseFloat(text)
  return Number.isFinite(n) ? n : null
}

function NumberField({
  id,
  label,
  suffix,
  value,
  step,
  onChange,
}: {
  id: string
  label: string
  suffix: string
  value: number | null
  step: number
  onChange: (value: number | null) => void
}) {
  return (
    <Field label={label} htmlFor={id}>
      <div className="flex items-center gap-2">
        <Input
          id={id}
          type="number"
          inputMode="decimal"
          step={step}
          min={0}
          value={value === null ? '' : String(value)}
          onChange={(e) => onChange(numberOrNull(e.target.value, step === 1))}
          className="w-28"
        />
        <span className="text-sm text-ink-muted">{suffix}</span>
      </div>
    </Field>
  )
}

/** Lighting's and AV's fields, which are the same. */
function EquipmentFields({ details, onChange }: { details: EquipmentDetails; onChange: (details: EquipmentDetails) => void }) {
  return (
    <>
      <Field label="Equipment" htmlFor="offering-equipment">
        <ChipListInput id="offering-equipment" values={details.equipment} placeholder="Uplights, a dance floor wash…" onChange={(equipment) => onChange({ ...details, equipment })} />
      </Field>
      <div className="flex flex-wrap gap-4">
        <NumberField id="offering-setup" label="Setup" suffix="minutes" step={1} value={details.setupMinutes} onChange={(setupMinutes) => onChange({ ...details, setupMinutes })} />
        <NumberField id="offering-teardown" label="Teardown" suffix="minutes" step={1} value={details.teardownMinutes} onChange={(teardownMinutes) => onChange({ ...details, teardownMinutes })} />
      </div>
    </>
  )
}

/**
 * The fields of one kind of vendor's package: a caterer's courses, a DJ's set
 * length. One case per service, and no default: a new service fails to
 * compile until it has its fields.
 */
export function OfferingDetailsFields({ value, onChange }: Props) {
  switch (value.service) {
    case 'catering': {
      const { details } = value
      return (
        <>
          <Field label="Courses" htmlFor="offering-courses">
            <ChipListInput id="offering-courses" values={details.courses} placeholder="Appetizers, entrée, dessert…" onChange={(courses) => onChange({ service: 'catering', details: { ...details, courses } })} />
          </Field>
          <fieldset className="m-0 space-y-2 border-0 p-0">
            <legend className="text-sm font-medium">Dietary needs it can meet</legend>
            <div className="flex flex-wrap gap-x-4 gap-y-2">
              {DIETARY_TAGS.map((tag) => (
                <label key={tag} className="inline-flex items-center gap-2 text-sm">
                  <Checkbox
                    checked={details.dietaryTags.includes(tag)}
                    onCheckedChange={(on) =>
                      onChange({
                        service: 'catering',
                        details: { ...details, dietaryTags: on ? [...details.dietaryTags, tag] : details.dietaryTags.filter((t) => t !== tag) },
                      })
                    }
                  />
                  {DIETARY_TAG_LABELS[tag]}
                </label>
              ))}
            </div>
          </fieldset>
        </>
      )
    }
    case 'photography': {
      const { details } = value
      return (
        <>
          <NumberField id="offering-hours" label="Coverage" suffix="hours" step={0.5} value={details.hours} onChange={(hours) => onChange({ service: 'photography', details: { ...details, hours } })} />
          <Field label="What they get" htmlFor="offering-deliverables">
            <ChipListInput id="offering-deliverables" values={details.deliverables} placeholder="Online gallery, 400 edited photos…" onChange={(deliverables) => onChange({ service: 'photography', details: { ...details, deliverables } })} />
          </Field>
        </>
      )
    }
    case 'entertainment': {
      const { details } = value
      return (
        <>
          <NumberField id="offering-set" label="Set length" suffix="hours" step={0.5} value={details.setLengthHours} onChange={(setLengthHours) => onChange({ service: 'entertainment', details: { ...details, setLengthHours } })} />
          <Field label="Equipment" htmlFor="offering-equipment">
            <ChipListInput id="offering-equipment" values={details.equipment} placeholder="Sound system, wireless mics…" onChange={(equipment) => onChange({ service: 'entertainment', details: { ...details, equipment } })} />
          </Field>
        </>
      )
    }
    case 'floral': {
      const { details } = value
      return (
        <Field label="Kind of piece" htmlFor="offering-piece">
          <Select
            id="offering-piece"
            value={details.piece ?? ''}
            onChange={(e) => {
              const piece = FLORAL_PIECES.find((p) => p === e.target.value) ?? null
              onChange({ service: 'floral', details: { piece } })
            }}
            className="w-48"
          >
            <option value="">Choose…</option>
            {FLORAL_PIECES.map((piece) => (
              <option key={piece} value={piece}>
                {FLORAL_PIECE_LABELS[piece]}
              </option>
            ))}
          </Select>
        </Field>
      )
    }
    case 'beauty': {
      const { details } = value
      return (
        <>
          <Field label="Services" htmlFor="offering-services">
            <ChipListInput id="offering-services" values={details.services} placeholder="Bridal hair, makeup…" onChange={(services) => onChange({ service: 'beauty', details: { ...details, services } })} />
          </Field>
          <div className="flex flex-wrap gap-x-6 gap-y-2">
            <label className="inline-flex items-center gap-2 text-sm">
              <Checkbox checked={details.trialIncluded} onCheckedChange={(trialIncluded) => onChange({ service: 'beauty', details: { ...details, trialIncluded } })} />
              Trial included
            </label>
            <label className="inline-flex items-center gap-2 text-sm">
              <Checkbox checked={details.onSite} onCheckedChange={(onSite) => onChange({ service: 'beauty', details: { ...details, onSite } })} />
              Comes to the venue
            </label>
          </div>
        </>
      )
    }
    case 'transportation': {
      const { details } = value
      return (
        <>
          <Field label="Vehicles" htmlFor="offering-vehicles">
            <ChipListInput id="offering-vehicles" values={details.vehicleTypes} placeholder="Vintage Rolls-Royce, party bus…" onChange={(vehicleTypes) => onChange({ service: 'transportation', details: { ...details, vehicleTypes } })} />
          </Field>
          <div className="flex flex-wrap gap-4">
            <NumberField id="offering-seats" label="Seats" suffix="people" step={1} value={details.seats} onChange={(seats) => onChange({ service: 'transportation', details: { ...details, seats } })} />
            <NumberField id="offering-minimum" label="Minimum" suffix="hours" step={0.5} value={details.minimumHours} onChange={(minimumHours) => onChange({ service: 'transportation', details: { ...details, minimumHours } })} />
          </div>
        </>
      )
    }
    case 'lighting':
      return <EquipmentFields details={value.details} onChange={(details) => onChange({ service: 'lighting', details })} />
    case 'av':
      return <EquipmentFields details={value.details} onChange={(details) => onChange({ service: 'av', details })} />
    case 'planning': {
      const { details } = value
      return (
        <div className="flex flex-wrap gap-4">
          <Field label="Kind of planning" htmlFor="offering-tier">
            <Select
              id="offering-tier"
              value={details.tier ?? ''}
              onChange={(e) => {
                const tier = PLANNING_TIERS.find((t) => t === e.target.value) ?? null
                onChange({ service: 'planning', details: { ...details, tier } })
              }}
              className="w-48"
            >
              <option value="">Choose…</option>
              {PLANNING_TIERS.map((tier) => (
                <option key={tier} value={tier}>
                  {PLANNING_TIER_LABELS[tier]}
                </option>
              ))}
            </Select>
          </Field>
          <NumberField id="offering-months" label="Involved for" suffix="months" step={1} value={details.monthsOfInvolvement} onChange={(monthsOfInvolvement) => onChange({ service: 'planning', details: { ...details, monthsOfInvolvement } })} />
        </div>
      )
    }
    default: {
      const unexpected: never = value
      return unexpected
    }
  }
}
